import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import FiltersPanel from "./FiltersPanel/FiltersPanel";
import QueriesTable from "./Table/QueriesTable";
import QueryDetailsDrawer from "./QueryDetailsDrawer/QueryDetailsDrawer";
import {
  getSupportInquiriesList,
  updateTicketStatus,
  deleteTicketRecord,
} from "../../Backend/querys";
import {
  pageShellClass,
  pageContentClass,
} from "../../Container/PageShell/pageShellClasses";

const Queries = () => {
  // --- STATE MANAGEMENT ---
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    category: "ALL",
    status: "ALL",
    priority: "ALL",
  });

  // --- DATA ORCHESTRATION ---
  const fetchSupportQueries = async () => {
    setLoading(true);
    const result = await getSupportInquiriesList();
    if (result.success) {
      // Map Firebase data directly to UI properties
      const formatted = result.data.map((item) => ({
        ...item,
        sender: item.name || "Anonymous",
        email: item.email || "No Email",
        formTitle: item.subject || "Support Ticket",
        description: item.message || "",
        formCategory: item.formType || "General Support",
        submittedAt: item.createdAt || new Date().toISOString(),
        isUnread: item.status === "pending",
      }));
      setQueries(formatted);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSupportQueries();
  }, []);

  // --- FILTERING ENGINE ---
  const filteredQueries = useMemo(() => {
    return queries.filter((q) => {
      const s = filters.search.toLowerCase();
      const matchSearch =
        q.sender.toLowerCase().includes(s) ||
        q.email.toLowerCase().includes(s) ||
        q.formTitle.toLowerCase().includes(s);

      const matchCategory =
        filters.category === "ALL" || q.formCategory === filters.category;
      const matchStatus =
        filters.status === "ALL" || q.status === filters.status;
      const matchPriority =
        filters.priority === "ALL" || q.priority === filters.priority;

      return matchSearch && matchCategory && matchStatus && matchPriority;
    });
  }, [queries, filters]);

  // --- CORE ACTIONS ---
 const handleAction = async (actionType, id, newValue) => {
    const target = queries.find((q) => q.id === id);

    // --- Optimistic Local UI Updates ---
    if (actionType === "delete") {
      const { isConfirmed } = await Swal.fire({
        title: "Delete Inquiry?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#EF4444",
      });
      if (!isConfirmed) return;
      
      setQueries((prev) => prev.filter((q) => q.id !== id));
      if (selectedQuery?.id === id) setSelectedQuery(null);
      
    } else if (actionType === "updateStatus") {
      setQueries((prev) =>
        prev.map((q) =>
          q.id === id
            ? { ...q, status: newValue, isUnread: newValue === "pending" }
            : q,
        ),
      );
    }

    // --- Backend Sync & External Actions ---
    try {
      if (actionType === "updateStatus") {
        await updateTicketStatus("support", id, { status: newValue });
      }
      
      if (actionType === "delete") {
        await deleteTicketRecord("support", id);
      }
      
      if (actionType === "reply") {
        // 1. Immediate Data Validation
        const email = target?.email;
        const senderName = target?.sender || "there";
        const subjectText = target?.formTitle || "Support Inquiry";

        if (!email || email === "No Email" || !email.includes("@")) {
          return Swal.fire({
            icon: "error",
            title: "Missing Email",
            text: "No valid return address found.",
            confirmButtonColor: "#0B1C2D",
          });
        }

        // 2. Construct the Content
        const subject = `RE: ${subjectText}`;
        const body = [
          `Hello ${senderName},`,
          "",
          `Thank you for reaching out regarding "${subjectText}".`,
          "",
          "We have received your message and our team is currently reviewing the details. We will provide an update shortly.",
          "",
          "Best regards,",
          "Support Team",
        ].join("\r\n");

        // 3. THE CRITICAL FIX: Direct URL trigger
        // encodeURIComponent is mandatory to handle spaces and symbols
        const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

        // Use window.open for a cleaner protocol trigger than location.href
        const mailWindow = window.open(mailtoUrl, "_self");

        // 4. Fallback check: If the window didn't trigger, use the anchor method
        if (
          !mailWindow ||
          mailWindow.closed ||
          typeof mailWindow.closed === "undefined"
        ) {
          const tempAnchor = document.createElement("a");
          tempAnchor.href = mailtoUrl;
          tempAnchor.style.display = "none";
          document.body.appendChild(tempAnchor);
          tempAnchor.click();
          document.body.removeChild(tempAnchor);
        }

        // 5. Success Feedback
        Swal.fire({
          icon: "success",
          title: "Opening Mail Client...",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      }
    } catch (e) {
      console.error("Sync Error:", e);
      // Revert on failure by re-fetching the source of truth
      fetchSupportQueries(); 
    }
  };

  return (
    <div className={pageShellClass}>
      <div className={pageContentClass}>
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 lg:mb-8">
        <div className="hidden lg:block">
          <h1 className="text-3xl font-black text-[#0B1C2D] tracking-tight uppercase">
            Querie Box
          </h1>
          <p className="text-slate-400 text-sm font-medium">
            Monitoring {queries.length} technical inquiries.
          </p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-72">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-300">
              search
            </span>
            <input
              type="text"
              placeholder="Search leads or tickets..."
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-slate-100"
              value={filters.search}
              onChange={(e) =>
                setFilters((p) => ({ ...p, search: e.target.value }))
              }
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-3 rounded-2xl border transition-all active:scale-95 ${showFilters ? "bg-[#0B1C2D] text-white" : "bg-white text-slate-400 border-slate-100"}`}
          >
            <span className="material-symbols-outlined">filter_list</span>
          </button>
        </div>
      </div>

      {/* FILTER PANEL */}
      {showFilters && (
        <FiltersPanel
          filters={filters}
          setFilters={setFilters}
          uniqueForms={[...new Set(queries.map((q) => q.formTitle))]}
        />
      )}

      {/* MAIN DATA TABLE */}
      <QueriesTable
        data={filteredQueries}
        loading={loading}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        onRowClick={setSelectedQuery}
        onAction={handleAction}
      />

      {/* DETAILS DRAWER */}
      <QueryDetailsDrawer
        query={selectedQuery}
        isOpen={!!selectedQuery}
        onClose={() => setSelectedQuery(null)}
        onAction={handleAction}
      />
      </div>
    </div>
  );
};

export default Queries;
